import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IPracticeMigration,
  ITranslationMap,
  type IGetRecordResponse,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  AccountCredit,
  Brand,
  Invoice,
  Patient,
  TransactionOperators,
  depositToLineItem,
  stafferToNamedDoc,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  IStaffer,
  InvoiceStatus,
  InvoiceType,
  TransactionProvider,
  type IBasePatient,
  type IPatient,
  type IPatientContactDetails,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  WithRef,
  getError,
  isSameRef,
  resolveSequentially,
  snapshotCombineLatest,
  sortByCreatedAt,
  toNamedDocument,
  type DocumentReference,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientDepositDestinationEntity,
  IDepositBuildData,
  IDepositJobData,
  IDepositMigrationData,
} from '../../../destination/entities/patient-deposits';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PRACTICE_MAPPING } from '../../../mappings/practices';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatientPaymentAllocation,
  PatientPaymentAllocationSourceEntity,
} from '../../source/entities/patient-payment-allocations';
import {
  CorePracticePatientPrepaymentLineItem,
  PatientPrepaymentLineItemSourceEntity,
} from '../../source/entities/patient-prepayment-line-items';
import {
  CorePracticePatientPrepayment,
  PatientPrepaymentSourceEntity,
} from '../../source/entities/patient-prepayments';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { CorePracticePaymentTypeMappingHandler } from '../mappings/payment-type-to-transaction-type';
import { CorePracticePracticeMappingHandler } from '../mappings/practices';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { getTransactions } from './patient-invoices';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

interface IJobData extends IDepositJobData<CorePracticePatient> {
  paymentTypes: WithPrimaryKey<ITranslationMap<object, TransactionProvider>>[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: WithRef<IStaffer>[];
}

export class PatientDepositDestinationEntity extends BasePatientDepositDestinationEntity<
  CorePracticePatient,
  IDepositJobData<CorePracticePatient>
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    prepayments: new PatientPrepaymentSourceEntity(),
    prepaymentLineItems: new PatientPrepaymentLineItemSourceEntity(),
    paymentAllocations: new PatientPaymentAllocationSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    practices: new CorePracticePracticeMappingHandler(),
    paymentTypes: new CorePracticePaymentTypeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const practitioners$ = brand$.pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand)))
    );
    const paymentTypes$ =
      this.customMappings.paymentTypes.getRecords(translationMap);
    const practices$ = this.customMappings.practices.getRecords(translationMap);

    return snapshotCombineLatest([
      practices$,
      staff$,
      practitioners$,
      paymentTypes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [practices, staff, practitioners, paymentTypes]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          practices,
          paymentTypes,
          staff,
          practitioners,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IJobData
  ): Promise<
    | IDepositMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(data.sourcePatient, 'No patient found');
      }

      const patient = await Firestore.getDoc(patientRef);

      const sourceDeposits = getPaidPrepayments(
        await this.sourceEntities.prepayments.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        )
      );

      const deposits = await resolveSequentially(
        sourceDeposits,
        async (deposit) => {
          const sourcePracticeId = deposit.data.locationId.toString();
          const practiceRef = await translationMap.getDestination<IPractice>(
            sourcePracticeId,
            PRACTICE_MAPPING.metadata.type
          );

          if (!practiceRef) {
            throw new Error(`No practice found for id: ${sourcePracticeId}`);
          }

          const sourceLineItems = (
            await this.sourceEntities.prepaymentLineItems.filterRecords(
              migration,
              'prepaymentId',
              deposit.data.id
            )
          ).filter((lineItem) => !lineItem.data.isVoided);

          const sourcePaymentAllocations =
            await this.sourceEntities.paymentAllocations.filterRecords(
              migration,
              'prepaymentId',
              deposit.data.id
            );

          const providerId = deposit.data.providerId.toString();
          const practitionerMap = data.staff.find(
            (staffer) => staffer.sourceIdentifier === providerId
          )?.destinationIdentifier;

          if (!practitionerMap) {
            throw new Error(
              `Couldn't resolve practitioner ${providerId ?? ''}`
            );
          }
          const practitioner = data.practitioners.find((searchPractitioner) =>
            isSameRef(searchPractitioner.ref, practitionerMap)
          );

          if (!practitioner) {
            throw new Error(
              `Couldn't resolve practitioner ${providerId ?? ''}`
            );
          }

          const depositData = await this._buildDepositData(
            migration,
            data,
            deposit,
            practiceRef,
            patient,
            sourceLineItems,
            sourcePaymentAllocations,
            practitioner
          );

          if (!depositData) {
            throw new Error(`Deposit ${deposit.record.uid} has no amount`);
          }

          return depositData;
        }
      );

      return {
        patientRef,
        deposits: sortBy(deposits, 'uid'),
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  private async _buildDepositData(
    migration: WithRef<IPracticeMigration>,
    data: IJobData,
    deposit: IGetRecordResponse<CorePracticePatientPrepayment>,
    practiceRef: DocumentReference<IPractice>,
    patient: WithRef<IPatient>,
    sourceLineItems: IGetRecordResponse<CorePracticePatientPrepaymentLineItem>[],
    sourcePaymentAllocations: IGetRecordResponse<CorePracticePatientPaymentAllocation>[],
    practitioner: WithRef<IStaffer>
  ): Promise<IDepositBuildData | undefined> {
    const practice = await Firestore.getDoc(practiceRef);
    const primaryContact = await Patient.resolvePrimaryContact(patient);
    const onBehalfOf = toAccountDetails(
      patient as IBasePatient & IPatientContactDetails
    );

    const transactions = await getTransactions(
      migration,
      deposit.data.id,
      deposit.record.translations.prepaymentDate,
      0,
      sourcePaymentAllocations,
      practiceRef,
      InvoiceType.Invoice,
      data.paymentTypes
    );
    const amount = deposit.data.total;
    const used = deposit.data.total - (deposit.data.remainingCredit ?? 0);
    const issuedAt = deposit.record.translations.prepaymentDate;

    const invoice = {
      ...Invoice.init({
        from: toAccountDetails(practice),
        to: primaryContact
          ? {
              ...toAccountDetails(primaryContact),
              onBehalfOf,
            }
          : onBehalfOf,
        practice: toNamedDocument(practice),
        items: sourceLineItems.map((lineItem) =>
          depositToLineItem(
            lineItem.data.itemCodeName ?? 'Deposit',
            lineItem.data.amount
          )
        ),
        createdAt: issuedAt,
        issuedAt,
      }),
      reference: deposit.data.id.toString(),
    };

    const status = getDepositInvoiceStatus(amount);

    const lastTransaction = new TransactionOperators(transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoice.paidAt = lastTransaction.createdAt;
    }

    Invoice.updateStatus(invoice, status, issuedAt);

    const accountCredit = AccountCredit.init({
      type: AccountCreditType.Deposit,
      description: 'Deposit',
      amount,
      used,
      reservedFor: {
        practitioner: stafferToNamedDoc(practitioner),
      },
      practiceRef,
    });

    if (amount !== Invoice.total(invoice)) {
      throw new Error(
        `Deposit ${
          deposit.data.id
        } has a mismatched amount to invoice. Invoice: ${Invoice.total(
          invoice
        )}, Account Credit: ${amount}`
      );
    }

    const uid = deposit.data.id.toString();

    return {
      uid,
      invoice,
      transactions,
      accountCredit: {
        ...accountCredit,
        createdAt: issuedAt,
      },
    };
  }
}

function getDepositInvoiceStatus(transactionAmount: number): InvoiceStatus {
  return transactionAmount > 0 ? InvoiceStatus.Paid : InvoiceStatus.Cancelled;
}

/**
 * Core Practice keeps prepayments that were requested but never paid, so only
 * paid ones become deposits. A partly paid prepayment fails the patient rather
 * than migrating as fully paid.
 */
export function getPaidPrepayments(
  prepayments: IGetRecordResponse<CorePracticePatientPrepayment>[]
): IGetRecordResponse<CorePracticePatientPrepayment>[] {
  const paidPrepayments = prepayments.filter(
    (prepayment) =>
      !prepayment.data.isVoided &&
      !prepayment.data.isDeleted &&
      prepayment.data.paid !== 0
  );

  const partlyPaid = paidPrepayments.find(
    (prepayment) => prepayment.data.paid < prepayment.data.total
  );
  if (partlyPaid) {
    throw new Error(
      `Prepayment ${partlyPaid.data.id} is only partly paid (${partlyPaid.data.paid} of ${partlyPaid.data.total})`
    );
  }

  return paidPrepayments;
}
